import {IndustryPropertyOptionsLookupStore} from "../lookup-stores/industry-property-options-lookup-store";
import {PartNumberLookupStore} from "../lookup-stores/part-number-lookup-store";
import {TagNameLookupStore} from "../lookup-stores/tag-name-lookup-store";
import {FormTitleLookupStore} from "../lookup-stores/form-title-lookup-store";
import {PartFormAssociationLookupStore} from "../lookup-stores/part-form-association-lookup-store";
import {VendorLookupStore} from "../lookup-stores/vendor-lookup-store";

/**
 * Shared execution context for one import transform run (fresh caches per instance).
 */
export class ImportTransformContext {
    constructor() {
        /** @type {Map<number, Set<string>>} */
        this._skippedFieldsByRow = new Map();
        /** @private */
        this._partNumberLookup = new PartNumberLookupStore();
        /** @private */
        this._vendorLookup = new VendorLookupStore();
        /** @private */
        this._industryPropertyOptionsLookup = new IndustryPropertyOptionsLookupStore();
        /** @private */
        this._tagNameLookup = new TagNameLookupStore();
        /** @private */
        this._formTitleLookup = new FormTitleLookupStore();
        /** @private */
        this._partFormAssociationLookup = new PartFormAssociationLookupStore();
    }

    /**
     * Returns `{ partIds, partInfo }` for the given uppercase part numbers.
     * Uses the in-memory run cache first; only missing numbers hit the network.
     *
     * @param {string[]} partNumbersUpper
     * @returns {Promise<{partIds: Record<string, *>, partInfo: Record<string, *>}>}
     */
    fetchPartInfoByPartNumbers(partNumbersUpper) {
        // TODO:  enable caching after we find a solution on how to update the part numbers cache from our preventative dialog's actions (like creating a new part Number or customer etc)
        return this._partNumberLookup.resolve(partNumbersUpper, true);
    }

    /**
     * Returns `{ vendorIds, vendorInfo }` for the given trimmed vendor codes.
     * Uses the in-memory run cache first; only missing codes hit the network.
     *
     * @param {string[]} vendorCodesTrimmed
     * @returns {Promise<{vendorIds: Record<string, *>, vendorInfo: Record<string, *>}>}
     */
    fetchVendorInfoByVendorCodes(vendorCodesTrimmed) {
        // TODO:  enable caching after we find a solution on how to update the vendor codes cache from our preventative dialog's actions (like creating a new vendor code or customer etc)
        return this._vendorLookup.resolve(vendorCodesTrimmed, true);
    }

    /**
     * Returns option lists keyed by property id (string).
     *
     * @param {Array<string|number>} propertyIds
     * @param {boolean} [disableCache=false]
     * @returns {Promise<Record<string, Array<{id: *, title: string}>>>}
     */
    fetchIndustryPropertyOptionsByPropertyIds(propertyIds, disableCache = false) {
        return this._industryPropertyOptionsLookup.resolve(propertyIds, disableCache);
    }

    /**
     * Merges a predefined option created in preventative correction into the import-run options cache.
     *
     * @param {string|number} propertyId
     * @param {{ id: *, title: string }} option
     */
    registerCreatedIndustryPropertyOption(propertyId, option) {
        this._industryPropertyOptionsLookup.registerCreatedOption(propertyId, option);
    }

    /**
     * Returns `{ tagIds, tagInfo }` for tag names within an industry.
     *
     * @param {string[]} tagNamesTrimmed
     * @param {string|number} industryId
     * @param {boolean} [disableCache=false]
     * @returns {Promise<{tagIds: Record<string, *>, tagInfo: Record<string, *>}>}
     */
    fetchTagInfoByNames(tagNamesTrimmed, industryId, disableCache = false) {
        return this._tagNameLookup.resolve(tagNamesTrimmed, industryId, disableCache);
    }

    /**
     * Merges a tag created in preventative correction into the import-run tag cache.
     *
     * @param {string|number} industryId
     * @param {{ id: *, name?: string }} tag
     */
    registerCreatedTag(industryId, tag) {
        this._tagNameLookup.registerCreatedTag(industryId, tag);
    }

    /**
     * Returns `{ formIds, formInfo }` keyed by exact normalized form title.
     *
     * @param {string[]} formTitlesTrimmed
     * @param {Array<{ id: string, title?: string }>} [preloadedForms]
     * @param {*} [formsApi]
     * @returns {Promise<{formIds: Record<string, *>, formInfo: Record<string, *>}>}
     */
    fetchFormInfoByTitles(formTitlesTrimmed, preloadedForms, formsApi) {
        return this._formTitleLookup.resolve(formTitlesTrimmed, preloadedForms, formsApi);
    }

    /**
     * Preloads existing part↔form associations for import rows.
     *
     * @param {Array<number|string>} partIds
     */
    fetchPartFormAssociationsByPartIds(partIds) {
        return this._partFormAssociationLookup.preloadForPartIds(partIds);
    }

    /**
     * @param {number|string} partId
     * @param {string} formId
     * @returns {{ partFormId: number, isRequired: boolean } | null}
     */
    getPartFormAssociation(partId, formId) {
        return this._partFormAssociationLookup.getAssociation(partId, formId);
    }

    /**
     * @param {number|string} partId
     * @param {string} formId
     * @param {number} partFormId
     * @param {boolean} [isRequired]
     */
    registerPartFormAssociation(partId, formId, partFormId, isRequired) {
        this._partFormAssociationLookup.registerAssociation(partId, formId, partFormId, isRequired);
    }

    /**
     * @param {Map<number, Set<string>>} skippedFieldsByRow
     */
    setSkippedFieldsByRow(skippedFieldsByRow) {
        this._skippedFieldsByRow =
            skippedFieldsByRow instanceof Map ? skippedFieldsByRow : new Map();
    }

    /**
     * @returns {Map<number, Set<string>>}
     */
    getSkippedFieldsByRow() {
        return this._skippedFieldsByRow;
    }

    clearSkippedFieldsByRow() {
        this._skippedFieldsByRow = new Map();
    }

    /**
     * Clears the cache of all of our transformers lookup stores.
     */
    clearCache() {
        this._partNumberLookup.clear();
        this._vendorLookup.clear();
        this._industryPropertyOptionsLookup.clear();
        this._tagNameLookup.clear();
        this._formTitleLookup.clear();
        this._partFormAssociationLookup.clear();
        this.clearSkippedFieldsByRow();
    }
}
